import { useEffect } from 'react';
import { useAuth } from '@aimsos/auth';
import { ShellAuthProvider } from '@aimsos/auth';
import { PeopleAccessApp } from '@aimsos/people-access';
import { usePeopleAccessI18n } from './usePeopleAccessI18n.js';

/**
 * Mounts the People & Access application inside the Core Shell.
 * Mirrors GovernanceEmbed: expose API base URLs via __AIMS_SHELL__ so the
 * lazy clients in people-access read them on first request.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT) instead
 * of useAuth0() directly, so this still mounts correctly on a
 * {slug}.{rootDomain} subdomain landed on via a cross-subdomain handoff,
 * where the Auth0 SDK itself has no session.
 */
const PeopleAccessEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const { ready: paI18nReady } = usePeopleAccessI18n();

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    window.__AIMS_SHELL__ = {
      mounted: true,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !paI18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <PeopleAccessApp noAccessRedirectPath="/" />
    </ShellAuthProvider>
  );
};

export default PeopleAccessEmbed;
